/**
 * 书签备份与还原 - 与浏览器扩展配合
 */

import { supabase } from '@ext-bookmark/shared/api';
import {
  requestBackupFromExtension,
  requestRestoreToExtension,
  type ChromeBookmarkNode,
  type RestoreFolder,
  type RestoreBookmark,
} from './extensionBridge';
import {
  listBookmarkFolders,
  listBookmarks,
  createBookmarkFolder,
  createBookmark,
  deleteBookmarkFolder,
} from './bookmarkService';
import { normalizeUrl } from '@ext-bookmark/shared/utils';

function isValidUrl(url: string): boolean {
  try {
    return /^https?:\/\//.test(url) || url.startsWith('ftp:') || url.startsWith('file:');
  } catch {
    return false;
  }
}

/** 书签栏可能的标题（Chrome/Edge 多语言） */
const BOOKMARKS_BAR_TITLES = ['书签栏', 'Bookmarks bar', 'Favorites', '收藏夹', '书签'];

/** 从 Chrome 树提取文件夹与书签（扁平化），根级统一放入「书签栏」文件夹 */
function extractFromChromeTree(
  roots: ChromeBookmarkNode[]
): { folders: Array<{ id: string; name: string; parentId: string | null; position: number }>; bookmarks: Array<{ folderId: string; name: string; url: string; position: number }> } {
  const folders: Array<{ id: string; name: string; parentId: string | null; position: number }> = [];
  const bookmarks: Array<{ folderId: string; name: string; url: string; position: number }> = [];

  const root = roots?.[0];
  const rootChildren = root?.children;
  if (!rootChildren?.length) return { folders, bookmarks };

  const bookmarksBar =
    rootChildren.find(
      (c) => c.id === '1' || BOOKMARKS_BAR_TITLES.some((t) => (c.title || '').toLowerCase().includes(t.toLowerCase()))
    ) ?? rootChildren[0];
  if (!bookmarksBar?.children) return { folders, bookmarks };

  const ROOT_ID = crypto.randomUUID();
  folders.push({ id: ROOT_ID, name: '书签栏', parentId: null, position: 0 });

  function walk(node: ChromeBookmarkNode, parentOurId: string, index: number) {
    if (node.url) {
      if (isValidUrl(node.url)) {
        bookmarks.push({
          folderId: parentOurId,
          name: (node.title || '未命名').trim(),
          url: normalizeUrl(node.url),
          position: index,
        });
      }
      return;
    }
    const ourId = crypto.randomUUID();
    folders.push({
      id: ourId,
      name: (node.title || '未命名').trim(),
      parentId: parentOurId,
      position: index,
    });
    (node.children || []).forEach((child, i) => walk(child, ourId, i));
  }

  bookmarksBar.children.forEach((child, i) => walk(child, ROOT_ID, i));

  if (folders.length <= 1 && bookmarks.length === 0) {
    collectFromEntireTree(roots, folders, bookmarks);
  }
  return { folders, bookmarks };
}

/** 备用：从整棵树收集（当主逻辑未匹配到书签栏时） */
function collectFromEntireTree(
  roots: ChromeBookmarkNode[],
  folders: Array<{ id: string; name: string; parentId: string | null; position: number }>,
  bookmarks: Array<{ folderId: string; name: string; url: string; position: number }>
) {
  const ROOT_ID = folders[0]?.id ?? crypto.randomUUID();
  if (folders.length === 0) folders.push({ id: ROOT_ID, name: '书签栏', parentId: null, position: 0 });

  function walkAll(node: ChromeBookmarkNode, parentId: string, index: number) {
    if (node.url && isValidUrl(node.url)) {
      bookmarks.push({
        folderId: parentId,
        name: (node.title || '未命名').trim(),
        url: normalizeUrl(node.url),
        position: index,
      });
      return;
    }
    const ourId = crypto.randomUUID();
    folders.push({
      id: ourId,
      name: (node.title || '未命名').trim(),
      parentId,
      position: index,
    });
    (node.children || []).forEach((child, i) => walkAll(child, ourId, i));
  }

  roots.forEach((root) => {
    (root.children || []).forEach((child, i) => walkAll(child, ROOT_ID, i));
  });
}

/** 备份：从浏览器读取书签并保存到 DB（覆盖当前用户数据） */
export async function backupFromBrowser(userId: string): Promise<{ error: string | null }> {
  if (!supabase) return { error: 'Supabase 未配置' };

  let tree: ChromeBookmarkNode[];
  try {
    const res = await requestBackupFromExtension();
    tree = Array.isArray(res) ? res : [];
  } catch (e) {
    return { error: e instanceof Error ? e.message : '无法连接扩展，请确保已安装 extBookMark 扩展' };
  }

  const { folders, bookmarks } = extractFromChromeTree(tree);

  const totalItems = folders.length + bookmarks.length;
  if (totalItems <= 1) {
    const rootChildren = tree?.[0]?.children?.length ?? 0;
    return {
      error:
        `扩展返回的书签数据为空（根节点子项: ${rootChildren}）。请确认：1) 扩展已安装并启用；2) 在 chrome://extensions 中刷新扩展后刷新本页；3) 书签栏（或收藏夹）中有书签或文件夹`,
    };
  }

  const { data: existingFolders } = await supabase
    .from('bookmark_folders')
    .select('id, parent_id')
    .eq('user_id', userId);
  const rootIds = (existingFolders || []).filter((f) => f.parent_id === null).map((f) => f.id);
  for (const id of rootIds) {
    const err = await deleteBookmarkFolder(id);
    if (err) return { error: err.message };
  }

  const folderIdMap = new Map<string, string>();

  function sortFoldersByParent(
    items: Array<{ id: string; name: string; parentId: string | null; position: number }>
  ): typeof items {
    const result: typeof items = [];
    const added = new Set<string | null>([null]);
    const remaining = new Map(items.map((x) => [x.id, x]));
    while (remaining.size > 0) {
      let addedAny = false;
      for (const [id, x] of remaining) {
        if (added.has(x.parentId)) {
          result.push(x);
          remaining.delete(id);
          added.add(id);
          addedAny = true;
        }
      }
      if (!addedAny) break;
    }
    return result;
  }

  const sortedFolders = sortFoldersByParent(folders);

  for (const f of sortedFolders) {
    const parentDbId = f.parentId ? folderIdMap.get(f.parentId) ?? null : null;
    const { folder, error } = await createBookmarkFolder(userId, {
      name: f.name,
      parentId: parentDbId || undefined,
      position: f.position,
    });
    if (error) return { error: error.message };
    if (folder) folderIdMap.set(f.id, folder.id);
  }

  for (const b of bookmarks) {
    const folderDbId = folderIdMap.get(b.folderId);
    if (!folderDbId) continue;
    const { error } = await createBookmark(userId, folderDbId, {
      name: b.name,
      url: b.url,
      position: b.position,
    });
    if (error) return { error: error.message };
  }

  return { error: null };
}

/** 还原：将 DB 中的书签写入浏览器（通过扩展） */
export async function restoreToBrowser(userId: string): Promise<{ error: string | null }> {
  const { folders, error: foldersErr } = await listBookmarkFolders(userId);
  if (foldersErr) return { error: foldersErr.message };

  const { bookmarks, error: bookmarksErr } = await listBookmarks(userId, null);
  if (bookmarksErr) return { error: bookmarksErr.message };

  const payload: { folders: RestoreFolder[]; bookmarks: RestoreBookmark[] } = {
    folders: folders.map((f) => ({
      id: f.id,
      name: f.name,
      parent_id: f.parent_id,
      position: f.position,
    })),
    bookmarks: bookmarks.map((b) => ({
      id: b.id,
      folder_id: b.folderId ?? '',
      name: b.name,
      url: b.url,
      note: b.note,
      position: b.position,
    })),
  };

  const validBookmarks = payload.bookmarks.filter((b) => b.folder_id && isValidUrl(b.url));
  payload.bookmarks = validBookmarks;

  try {
    await requestRestoreToExtension(payload);
  } catch (e) {
    return { error: e instanceof Error ? e.message : '还原失败' };
  }
  return { error: null };
}
